export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso)
    .toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(/\s*г\.?\s*$/u, '');
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function formatDaySeparator(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Сегодня';
  if (same(d, yesterday)) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatMoney(amount: string | number, currency = 'RUB'): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return String(amount);
  if (currency === 'USD') return `$${n.toFixed(0)}`;
  return `${n.toFixed(0)} ₽`;
}

export function subStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Активна';
    case 'lifetime':
      return 'Бессрочная';
    case 'keys_only':
      return 'Доступ по ключам';
    case 'expired':
      return 'Истекла';
    default:
      return 'Нет подписки';
  }
}

export function subStatusClass(status: string): string {
  switch (status) {
    case 'active':
    case 'lifetime':
    case 'keys_only':
      return 'badge badge-success';
    case 'expired':
      return 'badge badge-warning';
    default:
      return 'badge badge-muted';
  }
}

export function tierLabel(tier: string | null | undefined): string {
  switch ((tier || '').toLowerCase()) {
    case 'premium':
      return 'Premium';
    case 'default':
      return 'Default';
    default:
      return tier || '—';
  }
}

/** Percent off only when API gives a real regular_price (e.g. intro offer). */
export function periodDiscountPercent(period: {
  price: number;
  regular_price?: number | null;
}): number | null {
  if (period.regular_price == null || period.regular_price <= period.price) return null;
  const pct = Math.round((1 - period.price / period.regular_price) * 100);
  return pct > 0 ? pct : null;
}

export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function subscriptionExpiryLabel(
  until: string | null | undefined,
  status: string,
): string {
  if (status === 'lifetime' || status === 'keys_only') return 'Бессрочно';
  return formatDate(until);
}

export function subscriptionDaysLeftLabel(
  until: string | null | undefined,
  status: string,
): string {
  if (status === 'lifetime' || status === 'keys_only') return 'Бессрочно';
  const daysLeft = daysUntil(until);
  if (daysLeft !== null && status === 'active') {
    return `${daysLeft} ${pluralDays(daysLeft)}`;
  }
  return '—';
}

export function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'дней';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function pluralDevices(n: number): string {
  return pluralRu(n, 'устройство', 'устройства', 'устройств');
}

export function pluralProfiles(n: number): string {
  return pluralRu(n, 'профиль', 'профиля', 'профилей');
}

export function paymentHeadline(status: string): string {
  switch (status) {
    case 'processed':
    case 'success':
      return 'Платеж успешно выполнен';
    case 'pending':
      return 'Платеж в обработке';
    case 'failed':
      return 'Ошибка платежа';
    case 'refunded':
      return 'Платёж возвращён';
    case 'cancelled':
    case 'canceled':
      return 'Платёж отменён';
    default:
      return 'Последний платёж';
  }
}

export function subscriptionProgress(
  until: string | null | undefined,
  periodDays = 30,
  status?: string,
): number {
  if (status === 'lifetime' || status === 'keys_only') return 100;
  const left = daysUntil(until);
  if (left === null) return 0;
  return Math.min(100, Math.max(0, (left / Math.max(1, periodDays)) * 100));
}

export function paymentStatusLabel(status: string): string {
  switch (status) {
    case 'processed':
    case 'success':
      return 'Успешно';
    case 'pending':
      return 'В обработке';
    case 'failed':
      return 'Ошибка';
    case 'refunded':
      return 'Возврат';
    case 'cancelled':
    case 'canceled':
      return 'Отменён';
    default:
      return status;
  }
}

export function paymentStatusClass(status: string): string {
  switch (status) {
    case 'processed':
    case 'success':
      return 'success-text';
    case 'pending':
      return 'muted';
    case 'failed':
      return 'error-text';
    case 'refunded':
    case 'cancelled':
    case 'canceled':
      return 'warning-text';
    default:
      return 'muted';
  }
}

export function paymentStatusBadgeClass(status: string): string {
  switch (status) {
    case 'processed':
    case 'success':
      return 'badge badge-success';
    case 'pending':
      return 'badge badge-muted';
    case 'failed':
      return 'badge badge-danger';
    case 'refunded':
    case 'cancelled':
    case 'canceled':
      return 'badge badge-warning';
    default:
      return 'badge badge-muted';
  }
}

export function paymentStatusIconIsError(status: string): boolean {
  return status === 'failed' || status === 'cancelled' || status === 'canceled';
}

export function paymentStatusIconClass(status: string): string {
  if (paymentStatusIconIsError(status)) return 'payment-icon payment-icon-error';
  if (status === 'processed' || status === 'success') return 'payment-icon';
  return 'payment-icon payment-icon-muted';
}

export type SaveConfigResult = 'shared' | 'downloaded' | 'copied' | 'aborted' | 'failed';

function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/** Classic anchor download (desktop). */
export function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Navigate to a real attachment URL (works better in Android / WebView). */
export function navigateDownloadUrl(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Desktop: file download.
 * iOS: system share sheet — choose «Сохранить в Файлы», then from Files → OpenVPN.
 * Android: prefer Web Share with file, else caller should use token URL download.
 * OpenVPN usually is NOT in the Safari share list; that is expected.
 */
export async function saveConfigFile(filename: string, content: string): Promise<SaveConfigResult> {
  const file = new File([content], filename, { type: 'application/octet-stream' });

  if (isAppleTouchDevice() || isAndroidDevice()) {
    try {
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return 'shared';
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'aborted';
    }
    if (isAppleTouchDevice()) return 'failed';
    // Android without share: fall through to blob (caller may prefer token URL).
  }

  try {
    downloadTextFile(filename, content);
    return 'downloaded';
  } catch {
    try {
      await navigator.clipboard.writeText(content);
      return 'copied';
    } catch {
      return 'failed';
    }
  }
}

export function isIosLikeDevice(): boolean {
  return isAppleTouchDevice();
}
